/* camera-lag/app.js — /camera-lag-test page controller.

   File → PRE-FLIGHT (container read, < 1 s: codec, size, rotation, length, frame times, tags →
   the clock decision and every warning that does not need a decoded frame) → the window that
   fits this device's memory (the person picks it on a long clip) → ONE decode pass (WebCodecs;
   FFmpeg when the browser cannot) that keeps small gray frames in the Worker and a few preview
   pictures here → the clock (asked BEFORE any number when the file does not say it) → boxes
   (suggested only when sure, never a default guess) → analysis in the Worker → judge() decides
   what may be shown → the number, or the reason there is none.

   Every number on the page passes CameraLagDetect.judge(): automatic boxes, a tap, a drag, a
   key nudge, a changed clock — one road (QA 2026-11-04: rejected boxes still printed «296 мс»).

   Two colours carry one meaning everywhere: amber = the hand (thumb box, press marks),
   teal = the screen (screen box, change marks). */
(function () {
    'use strict';
    var CFG = window.__CL || {}, T = CFG.t || {}, SLUG = 'camera-lag-test';
    var $ = function (id) { return document.getElementById(id); };
    function t(k, r) {
        var s = T[k] != null ? String(T[k]) : k;
        if (r) Object.keys(r).forEach(function (n) { s = s.split(':' + n).join(r[n]); });
        return s;
    }
    function intl() { try { return window.__intlLocale ? window.__intlLocale() : (window.__LOCALE || 'en'); } catch (e) { return 'en'; } }
    function fmtMs(v, d) { if (v == null || !isFinite(v)) return '—'; d = d || 0; return (Math.round(v * Math.pow(10, d)) / Math.pow(10, d)).toLocaleString(intl(), { maximumFractionDigits: d, minimumFractionDigits: 0 }); }
    function fmtInt(v) { return Math.round(v).toLocaleString(intl()); }
    function ae(ev, p) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, p || {})); } catch (e) {} }
    function feat(f, p) { ae('feature_use', Object.assign({ f: f }, p || {})); }
    function stage(n, p) { try { if (window.__stage) window.__stage(n, p || {}); } catch (e) {} }
    // a page whose cached core predates the primitive still runs this file: keep a local way out
    function textWrite(e, t2) { if (!e) return; if (window.__setText) return window.__setText(e, t2); e.textContent = t2; }
    function lsGet(k, d) { try { var v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } }
    function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
    function rtl() { return document.documentElement.getAttribute('dir') === 'rtl'; }
    function round1(v) { return v == null || !isFinite(v) ? null : Math.round(v * 10) / 10; }
    function cancelError() { var e = new Error('cancelled'); e.name = 'AbortError'; e.code = 'USER_CANCELLED'; return e; }
    function sortedPts() { return S.info.sorted || (S.info.sorted = S.info.pts.slice().sort(function (a, b) { return a - b; })); }

    var AW = 160;
    var S = null;   // the current file
    var worker = null, wseq = 0, wwait = {};

    function freshState(file) {
        return { file: file, gen: (S ? S.gen + 1 : 1), phase: 'preflight', info: null, tb0: null, answer: null, askedAt: 0,
                 win: null, lane: null, n: 0, aw: AW, ah: 90, fullW: 0, fullH: 0, snaps: [], framePts: [], partial: null,
                 decoded: false, cancelled: false, tb: null, times: null, boxes: null, boxSource: null, boxMoves: 0, mseq: 0,
                 sug: null, result: null, verdict: null, marks: [], dup: 0, reported: {}, autoMedian: null, endRun: null, stateShown: null };
    }

    /* ── Worker ─────────────────────────────────────────────────────────────── */
    function ensureWorker() {
        if (worker) return worker;
        worker = new Worker(CFG.workerUrl);
        worker.onmessage = function (e) {
            var m = e.data || {}, w = wwait[m.id];
            if (!w) return;
            delete wwait[m.id];
            if (m.type === 'error') w.reject(new Error(m.message)); else w.resolve(m);
        };
        worker.onerror = function () { Object.keys(wwait).forEach(function (k) { wwait[k].reject(new Error('worker')); delete wwait[k]; }); };
        return worker;
    }
    function ask(msg, transfer) {
        var id = ++wseq; msg.id = id;
        return new Promise(function (resolve, reject) { wwait[id] = { resolve: resolve, reject: reject }; ensureWorker().postMessage(msg, transfer || []); });
    }

    /* ── Memory: how many analysis frames this device may hold ──────────────── */
    /* ONE owner of the budget, window.__memBudget: WebKit has no deviceMemory at all, and an
       iPhone tab has a fixed low ceiling whatever the phone's RAM (constrained()). A 160×90
       gray frame is 14.4 KB; 72 MB holds 5 000 of them — 21 s at 240 fps, the whole clip the
       guide asks for (QA M7: 3 600 frames = 15 s cut the iPhone's own guide in half). */
    function tight() { var B = window.__memBudget; try { return !!(B && ((B.constrained && B.constrained()) || (B.gb && B.gb() <= 2))); } catch (e) { return false; } }
    function storeBytes() {
        var B = window.__memBudget, gb = 4;
        try { if (B && typeof B.gb === 'function') gb = B.gb(); } catch (e) {}
        return tight() ? 72e6 : gb <= 4 ? 150e6 : 260e6;
    }
    function frameDims(w, h) {
        var aw = AW, ah = Math.max(16, Math.round(AW * h / w / 2) * 2);
        if (ah > 160) { ah = 160; aw = Math.max(16, Math.round(160 * w / h / 2) * 2); }
        return { aw: aw, ah: ah };
    }
    function budgetFrames(w, h) { var d = frameDims(w || 16, h || 9); return Math.max(600, Math.floor(storeBytes() / (d.aw * d.ah))); }

    /* ── Entry: a file ──────────────────────────────────────────────────────── */
    async function onFile(file) {
        if (!file) return;
        finishFile('new_file');
        if (S && S.abort) { try { S.abort(); } catch (e) {} }
        if (S) endRun();
        S = freshState(file);
        var gen = S.gen;
        resetView();
        stage('file_selected', { mb: Math.round(file.size / 1048576) });
        setProgress(0, t('reading'));
        try {
            await preflight(gen);
            if (gen !== S.gen) return;
            decideClockEarly();
            reportFile();
            renderPre();
            if (S.info.over) { setProgress(-1); showRange(); return; }
            await runDecode(gen);
        } catch (err) {
            if (!S || gen !== S.gen) return;
            failRead(err);
        }
    }
    function failRead(err) {
        endRun();
        setProgress(-1); show('clCancel', false);
        var kind = classify(err);
        S.phase = 'error';
        $('clWork').setAttribute('data-phase', 'error');
        showState('error', t('err_' + kind), t('err_' + kind + '_next'), kind === 'no_index' ? 'repair' : kind === 'generic' ? 'retry' : null);
        show('clView', false); show('clScrubWrap', false); show('clBoxBar', false);
        feat('cl_decode', { lane: S.lane || 'none', ms: 0, kept: S.n || 0, fail_stage: S.phase0 || 'preflight', fail_why: kind + ':' + String((err && err.message) || err).slice(0, 60) });
        if (kind === 'generic' || kind === 'codec') { try { if (window.__handleError) window.__handleError(err, 'camera_lag_read', { say: t('err_' + kind) }); } catch (e) {} }
    }
    function classify(err) {
        if (err && err.clKind) return err.clKind;
        var m = String((err && (err.code || err.message)) || err || '');
        if (/no-video-track/.test(m)) return 'audio_only';
        if (/too-few-frames/.test(m)) return 'too_short';
        if (/no-moov|mp4box/.test(m)) return S && S.magic === 'mp4' ? 'no_index' : 'not_video';
        if (/codec|unsupported|not supported/i.test(m)) return 'codec';
        return 'generic';
    }
    function typedErr(kind, msg) { var e = new Error(msg || kind); e.clKind = kind; return e; }

    /* ── Pre-flight: the container, before a single frame is decoded ───────── */
    async function sniff(file) {
        var b;
        try { b = new Uint8Array(await file.slice(0, 16).arrayBuffer()); } catch (e) { return 'unknown'; }
        if (b.length < 12) return 'unknown';
        var str = function (i, n) { return String.fromCharCode.apply(null, Array.prototype.slice.call(b, i, i + n)); };
        var s4 = str(4, 4), brand = str(8, 4);
        if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image';
        if (b[0] === 0x89 && str(1, 3) === 'PNG') return 'image';
        if (str(0, 3) === 'GIF' || str(0, 4) === '%PDF' || (str(0, 4) === 'RIFF' && brand === 'WEBP')) return 'image';
        if (s4 === 'ftyp' && /^(heic|heix|heim|heis|mif1|msf1|avif|avis)/.test(brand)) return 'image';
        if (s4 === 'ftyp' || s4 === 'moov' || s4 === 'mdat' || s4 === 'wide' || s4 === 'free' || s4 === 'skip') return 'mp4';
        if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return 'mkv';
        if (str(0, 4) === 'RIFF') return brand === 'AVI ' ? 'avi' : 'audio';
        if (str(0, 3) === 'ID3' || (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) || str(0, 4) === 'fLaC' || str(0, 4) === 'OggS') return 'audio';
        if (b[0] === 0x47) return 'ts';
        return 'unknown';
    }
    async function ensureMp4Box(V) {
        if (window.MP4Box) return;
        if (V && typeof V.loadMp4Box === 'function') await V.loadMp4Box();
        if (!window.MP4Box) throw new Error('mp4box-missing');
    }
    async function preflight(gen) {
        var file = S.file, t0 = Date.now();
        S.magic = await sniff(file);
        if (S.magic === 'image') throw typedErr('not_video_image');
        if (S.magic === 'audio') throw typedErr('audio_only');
        S.tags = {};
        try { S.tags = window.CameraLagMeta ? await window.CameraLagMeta.read(file) : {}; } catch (e) {}
        if (gen !== S.gen) return;
        var V = window.VideoMergeWebCodecs, info = { container: S.magic, codec: '', w: 0, h: 0, rot: 0, durS: 0, frames: 0, pts: null, d: null, decodable: null };
        if (V && (S.magic === 'mp4' || S.magic === 'mkv')) {
            try {
                if (S.magic === 'mp4') await ensureMp4Box(V);
                var d = await V.demuxClip(file, false, { matroska: true, slug: SLUG });
                if (!d || !d.samples || !d.samples.length) throw new Error('no-samples');
                info.d = d; info.codec = String(d.codec || '');
                info.w = d.displayWidth || (d.vtrack && d.vtrack.video && d.vtrack.video.width) || 0;
                info.h = d.displayHeight || (d.vtrack && d.vtrack.video && d.vtrack.video.height) || 0;
                info.rot = d.rotationDeg || 0;
                info.pts = V.presentationTimesUs(d);
                info.frames = d.samples.length;
                var so = info.pts.slice().sort(function (a, b) { return a - b; });
                info.durS = (so[so.length - 1] - so[0]) / 1e6 + (so.length > 1 ? (so[1] - so[0]) / 1e6 : 0);
                /* A file cut short with its index at the front (QA C2: moov first, mdat cut): the
                   table promises frames whose bytes are not there. Keep what is. */
                if (d.lazy) {
                    var lastOk = -1;
                    for (var i = 0; i < d.samples.length; i++) { var sm = d.samples[i]; if (sm.offset != null && sm.offset + sm.size > file.size) break; lastOk = i; }
                    if (lastOk < d.samples.length - 1) {
                        /* stop 16 samples short of the hole: a reordered frame shown before the cut
                           can need bytes after it, and the decoder then fails and retries (QA C2) */
                        var nOk = Math.max(0, lastOk + 1 - 16);
                        var kept = info.pts.slice(0, nOk).sort(function (a, b) { return a - b; });
                        info.cutAt = { n: nOk, us: kept.length ? kept[kept.length - 1] - so[0] : 0 };
                    }
                }
                if (typeof VideoDecoder === 'undefined') info.decodable = false;
                else if (V.decoderConfig && V.hasUsableDims && V.hasUsableDims(d)) {
                    try { var sup = await VideoDecoder.isConfigSupported(V.decoderConfig(d)); info.decodable = !!(sup && sup.supported); } catch (e) { info.decodable = null; }
                }
            } catch (e) {
                var em = String((e && e.message) || e);
                if (/no-video-track/.test(em)) throw typedErr('audio_only', em);
                if (S.magic === 'mp4' && /no-moov|mp4box|no-samples/.test(em)) info.noIndex = true;
                info.demuxError = em.slice(0, 60);
            }
        }
        if (!info.pts) {
            /* No sample table: a container the demuxers do not know, or a broken one. A mp4 with
               no index (QA C1: the camera died before writing moov) is a repair job, not ours. */
            if (info.noIndex) throw typedErr('no_index');
            var pr = await ffmpegProbe(file);
            if (gen !== S.gen) return;
            if (!pr) throw typedErr('not_video');
            if (!pr.video) throw typedErr(pr.audio ? 'audio_only' : 'not_video');
            info.w = pr.w; info.h = pr.h; info.durS = pr.durS; info.codec = pr.codec; info.fpsProbe = pr.fps; info.rot = pr.rot || 0;
            info.frames = Math.max(1, Math.round(pr.durS * pr.fps));
            info.pts = []; for (var k = 0; k < info.frames; k++) info.pts.push(Math.round(k * 1e6 / pr.fps));
            info.decodable = false; info.ptsFromProbe = true;
        }
        if (!info.w || !info.h) { info.w = 1280; info.h = 720; }
        info.budget = budgetFrames(info.w, info.h);
        var avail = info.cutAt ? info.cutAt.n : info.frames;
        info.over = avail > info.budget;
        info.ms = Date.now() - t0;
        S.info = info;
    }
    async function ffmpegProbe(file) {
        try {
            if (typeof window.__ensureFFmpeg !== 'function') return null;
            var load = window.__ensureFFmpeg();
            await Promise.race([load, new Promise(function (_, rej) { setTimeout(function () { rej(new Error('ffmpeg-load-timeout')); }, 60000); })]);
            var FP = window.FFmpegProcessor;
            if (!FP || typeof FP.probeStreaming !== 'function') return null;
            var log = String(await Promise.race([FP.probeStreaming(file), new Promise(function (r) { setTimeout(function () { r(''); }, 20000); })]) || '');
            var v = log.match(/Stream #\d+:\d+[^\n]*Video:\s*([a-z0-9_]+)[^\n]*?(\d{2,5})x(\d{2,5})[^\n]*?([\d.]+)\s*fps/i);
            var a = /Stream #\d+:\d+[^\n]*Audio:/i.test(log);
            var dur = log.match(/Duration:\s*(\d+):(\d+):([\d.]+)/);
            var rot = log.match(/rotation of (-?[\d.]+) degrees/);
            if (!v) return { video: false, audio: a };
            var w = +v[2], h = +v[3], r = rot ? ((360 - Math.round(+rot[1])) % 360 + 360) % 360 : 0;
            if (r === 90 || r === 270) { var tmp = w; w = h; h = tmp; }
            return { video: true, audio: a, codec: v[1], w: w, h: h, fps: +v[4], rot: r, durS: dur ? (+dur[1]) * 3600 + (+dur[2]) * 60 + (+dur[3]) : 0 };
        } catch (e) { return null; }
    }
    function reportFile() {
        var i = S.info, tags = S.tags || {}, tb = S.tb0;
        var name = String(S.file.name || ''), ext = (name.match(/\.([a-z0-9]{2,5})$/i) || [])[1] || '';
        feat('cl_file', { container: (ext || i.container).toLowerCase(), codec: String(i.codec).slice(0, 16), w: i.w, h: i.h, rot: i.rot,
                          dur_s: round1(i.durS), frames: i.frames, stream_fps: tb ? round1(tb.streamFps) : null, tag_fps: tags.captureFps || null,
                          ramp: tb && tb.ramp ? 1 : 0, make: tags.make ? String(tags.make).slice(0, 16) : null, model: tags.model ? String(tags.model).slice(0, 24) : null,
                          mb: Math.round(S.file.size / 1048576), dec: i.decodable == null ? null : (i.decodable ? 1 : 0), cut: i.cutAt ? 1 : 0, over: i.over ? 1 : 0, pre_ms: i.ms });
    }
    function decideClockEarly() {
        S.tb0 = window.CameraLagTimebase.decide({ ptsUs: S.info.pts, tags: S.tags });
        renderClock();
        if (S.tb0.mode === 'ask') { S.askedAt = Date.now(); setPhase('ask'); }
    }

    /* ── The pre-flight card: what the file is, said before the wait ─────────── */
    function renderPre() {
        var i = S.info, ul = $('clPreWarn'), warn = [];
        ul.textContent = '';
        textWrite($('clPreLine'), t('pre_line', { w: i.w, h: i.h, s: fmtMs(i.durS, 1), n: fmtInt(i.frames) }));
        if (i.decodable === false && !i.ptsFromProbe) warn.push(t('pre_codec', { codec: codecName(i.codec) }));
        if (i.ptsFromProbe) warn.push(t('pre_foreign'));
        if (i.cutAt) warn.push(t('pre_cut', { s: fmtMs(i.cutAt.us / 1e6, 1), total: fmtMs(i.durS, 1) }));
        if (i.durS && i.durS < 3) warn.push(t('pre_short'));
        warn.forEach(function (w) { var li = document.createElement('li'); li.textContent = w; ul.appendChild(li); });
        ul.hidden = !warn.length;
        show('clPre', true);
    }
    function codecName(c) { c = String(c || '').toLowerCase(); return /^(hvc1|hev1|hevc)/.test(c) ? 'HEVC (H.265)' : /^(avc|h264)/.test(c) ? 'H.264' : /^(vp09|vp9)/.test(c) ? 'VP9' : /^(av01|av1)/.test(c) ? 'AV1' : (c.split('.')[0] || '?').toUpperCase(); }

    /* A clip longer than the memory budget: the person chooses WHICH stretch, before the wait
       (QA M7: a 2-minute clip silently lost 33 of its 60 presses). */
    function showRange() {
        var i = S.info, fps = i.frames / Math.max(0.1, i.durS), winS = i.budget / fps, availS = i.cutAt ? i.cutAt.us / 1e6 : i.durS;
        S.rangeWinS = Math.max(1, Math.min(availS, winS));
        var r = $('clRangeStart');
        r.min = '0'; r.max = String(Math.max(0, Math.floor((availS - S.rangeWinS) * 10) / 10)); r.step = '0.1'; r.value = '0';
        updateRangeText();
        show('clRange', true);
        if (!(S.tb0 && S.tb0.mode === 'ask')) setPhase('range');
        feat('cl_range_shown', { dur_s: round1(i.durS), win_s: round1(S.rangeWinS) });
    }
    function updateRangeText() {
        if (!S || !S.info) return;
        var a = +$('clRangeStart').value || 0;
        textWrite($('clRangeText'), t('range_text', { a: fmtMs(a, 1), b: fmtMs(a + S.rangeWinS, 1), total: fmtMs(S.info.durS, 1), win: fmtMs(S.rangeWinS, 0) }));
    }
    function rangeGo() {
        if (!S || !S.info || S.decoding) return;
        var a = +$('clRangeStart').value || 0, order = sortedPts(), k0 = 0, aUs = order[0] + a * 1e6;
        while (k0 < order.length - 1 && order[k0] < aUs) k0++;
        S.win = { startUs: order[k0], k0: k0 };
        show('clRange', false);
        feat('cl_range_go', { start_s: round1(a) });
        var gen = S.gen;
        runDecode(gen).catch(function (err) { if (S && gen === S.gen) failRead(err); });
    }

    /* ── Decode: one pass, two outputs ───────────────────────────────────────── */
    async function runDecode(gen) {
        var i = S.info, V = window.VideoMergeWebCodecs, t0 = Date.now(), ok = false, why = null;
        S.decoding = true;
        if (!(S.tb0 && S.tb0.mode === 'ask' && S.answer == null)) setPhase('decoding');
        else if (S.answer == null) setPhase('ask');
        S.endRun = null;
        try { S.endRun = window.__runMark && window.__runMark.begin(); } catch (e) {}
        try { if (window.__acETA) window.__acETA.beginStage('cl-decode'); } catch (e) {}
        show('clCancel', true);
        S.phase0 = 'decode';
        try {
            if (V && i.d && i.decodable !== false && typeof VideoDecoder !== 'undefined' && !window.__CL_FORCE_FFMPEG) {
                try { await decodeWebCodecs(gen, V); ok = true; S.lane = 'webcodecs'; }
                catch (e) {
                    if (gen !== S.gen || S.cancelled) throw e;
                    why = String((e && e.message) || e).slice(0, 60);
                    // frames already decoded up to a broken stretch are kept (QA M10: C2 threw 1 930 good frames away)
                    if (S.n >= 240 && S.framePts.length === S.n) { ok = true; S.lane = 'webcodecs'; S.partial = { why: i.cutAt ? 'cut' : 'decode', s: (S.framePts[S.n - 1] - S.framePts[0]) / 1e6 }; }
                    else { feat('lane_fallback', { why: why }); resetStore(); }
                }
            }
            if (!ok) { await decodeFfmpeg(gen); S.lane = 'ffmpeg'; }
        } catch (err) {
            S.decoding = false;
            show('clCancel', false);
            if (!S || gen !== S.gen) return;
            if (S.cancelled) return afterCancel();
            endRun();
            throw err;
        }
        S.decoding = false;
        show('clCancel', false);
        if (gen !== S.gen) return;
        if (S.cancelled) return afterCancel();
        endRun();
        if (S.n < 10) throw typedErr('too_short', 'too-few-frames');
        S.decoded = true;
        feat('cl_decode', { lane: S.lane, ms: Date.now() - t0, decoded_fps: Math.round(S.n / Math.max(0.001, (Date.now() - t0) / 1000)),
                            kept: S.n, truncated_s: S.partial ? round1(S.partial.s) : null, win: S.win ? 1 : 0, fb: why });
        setProgress(-1);
        try { S.dup = (await ask({ type: 'dup' })).share || 0; } catch (e) {}
        if (gen !== S.gen) return;
        setupView();
        if (S.tb0.mode === 'ask' && S.answer == null) { renderWaitAnswer(); return; }
        await measure(gen);
    }
    function endRun() {
        if (S && S.endRun) { try { S.endRun(); } catch (e) {} S.endRun = null; }
        try { if (window.__acETA && window.__acETA.endStage) window.__acETA.endStage('cl-decode'); } catch (e) {}
    }
    function afterCancel() {
        endRun(); setProgress(-1);
        var keptS = S.framePts.length > 1 ? (S.framePts[S.framePts.length - 1] - S.framePts[0]) / 1e6 : 0;
        feat('cl_cancel', { kept_s: round1(keptS), lane: S.lane || 'webcodecs' });
        /* «Stop» after a few seconds of a long read means «that is enough»: the frames already
           read are measured. Before two seconds there is nothing to measure. */
        if (S.n >= 240 && S.framePts.length === S.n) {
            S.cancelled = false; S.partial = { why: 'cancel', s: keptS }; S.decoded = true; S.lane = S.lane || 'webcodecs';
            setupView();
            if (S.tb0.mode === 'ask' && S.answer == null) { renderWaitAnswer(); return; }
            measure(S.gen);
            return;
        }
        S.phase = 'error';
        $('clWork').setAttribute('data-phase', 'error');
        showState('error', t('cancelled'), t('cancelled_next'), 'retry');
    }
    function resetStore() { S.n = 0; S.snaps = []; S.framePts = []; }
    function setupStore(w, h, capacity) {
        S.fullW = w; S.fullH = h;
        var d = frameDims(w, h); S.aw = d.aw; S.ah = d.ah;
        return ask({ type: 'init', w: S.aw, h: S.ah, capacity: capacity });
    }
    function snapCount() { return tight() ? 32 : 48; }
    function snapWidth() { return tight() ? 480 : 960; }
    function decodeProgress(k, total) {
        var frac = k / Math.max(1, total), eta = null;
        try { if (window.__acETA) { window.__acETA.updateRate('cl-decode', frac); eta = window.__acETA.remainingSec('cl-decode'); } } catch (e) {}
        var txt = t('reading_frames', { n: fmtInt(k), total: fmtInt(total) });
        if (eta) txt += ' · ' + (eta >= 90 ? t('eta_min', { n: Math.round(eta / 60) }) : t('eta_s', { n: Math.max(5, Math.round(eta / 5) * 5) }));
        setProgress(frac, txt);
    }
    async function decodeWebCodecs(gen, V) {
        var d = S.info.d, i = S.info, order = sortedPts();
        var avail = i.cutAt ? i.cutAt.n : order.length, k0 = S.win ? S.win.k0 : 0;
        var total = Math.min(avail - k0, i.budget);
        var endUs = (k0 + total < order.length) ? order[k0 + total - 1] : null;
        var startUs = S.win ? S.win.startUs : 0;
        var cvs = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(16, 16) : document.createElement('canvas');
        var ctx = null, batch = null, bi = 0, BATCH = 48, fs = 0;
        var stream = new V.DecodeStream(d, startUs, endUs, { hwm: 4 });
        var snapEvery = Math.max(1, Math.floor(total / snapCount())), k = 0;
        S.abort = function () { S.cancelled = true; try { stream.close(); } catch (e) {} };
        try {
            for (;;) {
                if (gen !== S.gen || S.cancelled) break;
                var f = await stream.next();
                if (!f) break;
                if (S.win && f.timestamp < startUs) { f.close(); continue; }
                if (k >= total) { f.close(); break; }
                if (!ctx) {
                    var w = f.displayWidth || f.codedWidth, h = f.displayHeight || f.codedHeight;
                    await setupStore(w, h, total);
                    cvs.width = S.aw; cvs.height = S.ah;
                    ctx = cvs.getContext('2d', { willReadFrequently: true });
                    fs = S.aw * S.ah; batch = new Uint8Array(fs * BATCH);
                }
                ctx.drawImage(f, 0, 0, S.aw, S.ah);
                var px = ctx.getImageData(0, 0, S.aw, S.ah).data, o = bi * fs;
                for (var q = 0, j = 0; q < fs; q++, j += 4) batch[o + q] = (px[j] * 77 + px[j + 1] * 150 + px[j + 2] * 29) >> 8;
                S.framePts.push(f.timestamp);
                if (k % snapEvery === 0) { var sb = await snapshot(f); S.snaps.push({ k: k, bmp: sb }); livePreview(sb); }
                f.close();
                k++; bi++; S.n = k;
                if (bi === BATCH) { var b = batch.buffer; worker.postMessage({ type: 'frames', buf: b, count: bi }, [b]); batch = new Uint8Array(fs * BATCH); bi = 0; }
                if (k % 24 === 0) { decodeProgress(k, total); await yieldTick(); }
            }
        } finally {
            try { stream.close(); } catch (e) {}
            if (bi && batch) { var b2 = batch.buffer; worker.postMessage({ type: 'frames', buf: b2, count: bi }, [b2]); bi = 0; }
            S.n = k; S.abort = null;
        }
        if (S.cancelled) throw cancelError();
        if (k < 10) throw new Error('too-few-frames');
        if (i.cutAt && k0 + k >= avail - 1) S.partial = { why: 'cut', s: (S.framePts[k - 1] - S.framePts[0]) / 1e6 };
    }
    /* A preview picture of one frame. createImageBitmap(VideoFrame, {resize}) is the fast road; it
       throws on some WebKit builds and returns an EMPTY bitmap on others (grey boxes to place on) —
       both go through a canvas instead. */
    var bitmapLane = 'bitmap';
    async function snapshot(f) {
        var w = f.displayWidth || f.codedWidth, h = f.displayHeight || f.codedHeight, sc = Math.min(1, snapWidth() / Math.max(w, h));
        var sw = Math.max(1, Math.round(w * sc)), sh = Math.max(1, Math.round(h * sc));
        if (bitmapLane === 'bitmap' && typeof createImageBitmap === 'function') {
            try {
                var bmp = await createImageBitmap(f, { resizeWidth: sw, resizeHeight: sh, resizeQuality: 'medium' });
                if (S.bitmapChecked) return bmp;
                S.bitmapChecked = true;
                if (!blankBitmap(bmp)) return bmp;
                bitmapLane = 'canvas'; feat('cl_bitmap_blank');
            } catch (e) { bitmapLane = 'canvas'; feat('cl_bitmap_failed', { why: String((e && e.message) || e).slice(0, 40) }); }
        }
        var c = document.createElement('canvas'); c.width = sw; c.height = sh;
        try { c.getContext('2d').drawImage(f, 0, 0, sw, sh); } catch (e) { return null; }
        return c;
    }
    function blankBitmap(bmp) {
        try {
            var c = document.createElement('canvas'); c.width = 8; c.height = 8;
            var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(bmp, 0, 0, 8, 8);
            var d = x.getImageData(0, 0, 8, 8).data, s = 0;
            for (var i = 0; i < d.length; i += 4) s += d[i + 3];
            return s === 0;
        } catch (e) { return false; }
    }
    function yieldTick() { return window.__yieldTick ? window.__yieldTick(0) : new Promise(function (r) { setTimeout(r, 0); }); }
    // the picture moves while the clip is read: a long read is not a black box (QA m7)
    function livePreview(src) {
        if (!src) return;
        var cv = $('clFrame'), w = src.width, h = src.height;
        if (cv.width !== w) cv.width = w; if (cv.height !== h) cv.height = h;
        $('clView').style.aspectRatio = w + ' / ' + h;
        try { cv.getContext('2d').drawImage(src, 0, 0, w, h); } catch (e) {}
        show('clView', true);
    }

    /* The fallback lane: FFmpeg-WASM decodes what this browser's WebCodecs cannot (HEVC without a
       platform decoder, no WebCodecs at all, a container the demuxers do not know). __ensureFFmpeg()
       resolves TRUE once the engine is on the page — the processor is window.FFmpegProcessor
       (QA B1: `FP = await __ensureFFmpeg()` → «FP.process is not a function» on every such file). */
    async function decodeFfmpeg(gen) {
        if (typeof window.__ensureFFmpeg !== 'function') throw typedErr('codec', 'no-ffmpeg');
        setProgress(0, t('reading_engine'));
        S.phase0 = 'ffmpeg_load';
        await window.__ensureFFmpeg();
        var FP = window.FFmpegProcessor;
        if (!FP || typeof FP.process !== 'function') throw typedErr('codec', 'no-ffmpeg-processor');
        if (gen !== S.gen) return;
        var i = S.info, order = sortedPts();
        var avail = i.cutAt ? i.cutAt.n : order.length, k0 = S.win ? S.win.k0 : 0;
        var total = Math.min(avail - k0, i.budget);
        await setupStore(i.w, i.h, total);
        S.phase0 = 'ffmpeg_run';
        var pre = S.win ? ['-ss', ((order[k0] - order[0]) / 1e6).toFixed(3)] : [];
        var args = ['-map', '0:v:0', '-frames:v', String(total), '-vf', 'scale=' + S.aw + ':' + S.ah + ':flags=area,format=gray', '-fps_mode', 'passthrough', '-an', '-f', 'rawvideo'];
        S.abort = function () { S.cancelled = true; try { FP.cancel(); } catch (e) {} };
        try { if (typeof FP.onProgress === 'function') FP.onProgress(function (r) { decodeProgress(Math.round(r * total), total); }); } catch (e) {}
        var out;
        try { out = await FP.process(S.file, args, 'cl-frames.raw', undefined, pre); }
        catch (e) { if (S.cancelled) throw cancelError(); throw typedErr('codec', 'ffmpeg:' + String((e && e.message) || e).slice(0, 50)); }
        finally { S.abort = null; try { FP.onProgress(function () {}); } catch (e) {} }
        if (gen !== S.gen) return;
        var blob = out && out.blob ? out.blob : out;
        var buf = new Uint8Array(await blob.arrayBuffer());
        var fs = S.aw * S.ah, n = Math.min(Math.floor(buf.length / fs), total);
        if (n < 10) throw typedErr('codec', 'ffmpeg-no-frames');
        var copy = buf.slice(0, n * fs).buffer;
        worker.postMessage({ type: 'frames', buf: copy, count: n }, [copy]);
        S.n = n;
        S.framePts = order.slice(k0, k0 + n);
        if (S.framePts.length < n) { var step = 1e6 / (i.fpsProbe || 30); while (S.framePts.length < n) S.framePts.push(S.framePts.length * step); }
        if (n < total - 2) S.partial = { why: 'cut', s: (S.framePts[n - 1] - S.framePts[0]) / 1e6 };
        else if (i.cutAt) S.partial = { why: 'cut', s: (S.framePts[n - 1] - S.framePts[0]) / 1e6 };
        // preview pictures: the gray frames, upscaled — enough to place two boxes
        var every = Math.max(1, Math.floor(n / 40));
        for (var k2 = 0; k2 < n; k2 += every) {
            var c2 = document.createElement('canvas'); c2.width = S.aw; c2.height = S.ah;
            var cx = c2.getContext('2d'), id = cx.createImageData(S.aw, S.ah);
            for (var q = 0; q < fs; q++) { var g = buf[k2 * fs + q]; id.data[q * 4] = id.data[q * 4 + 1] = id.data[q * 4 + 2] = g; id.data[q * 4 + 3] = 255; }
            cx.putImageData(id, 0, 0);
            S.snaps.push({ k: k2, bmp: c2 });
        }
        livePreview(S.snaps[S.snaps.length >> 1].bmp);
    }

    /* ── The clock ──────────────────────────────────────────────────────────── */
    function ptsOfFrames() {
        if (!S.decoded && S.info) return S.info.pts;
        if (S.framePts && S.framePts.length === S.n && S.n) return S.framePts;
        var a = []; for (var i = 0; i < S.n; i++) a.push(i * 1e6 / 30); return a;
    }
    /* The clock for the decoded frames. For a file that does not say its rate, NOTHING is shown
       until the person answers (QA B2: an untagged 30 fps file taken as 240 → «4 мс — очень
       быстрый»). An ordinary 30 fps video is refused — a frame is 33 ms; 60 fps is measured with
       its precision said out loud. */
    function clockFor(answer) {
        var pts = ptsOfFrames(), tags = S.tags || {};
        if (answer == null) return window.CameraLagTimebase.decide({ ptsUs: pts, tags: tags });
        return window.CameraLagTimebase.decide({ ptsUs: pts, tags: tags, userFps: answer });
    }
    function setAnswer(v) {
        if (!S || !S.info || !S.tb0) return;
        var first = S.answer == null && S.tb0.mode === 'ask';
        S.answer = v === 'file' ? null : v;
        S.clockOpen = false; S.tb = null;
        feat('cl_clock', { decision: S.tb0.mode + ':' + (S.tb0.source || ''), asked: S.tb0.mode === 'ask' ? 1 : 0, answer: String(v),
                           answer_ms: first && S.askedAt ? Date.now() - S.askedAt : null, stream_fps: round1(S.tb0.streamFps) });
        renderClock();
        if (S.decoded) { measure(S.gen); return; }
        if (refusedNormal()) { renderRefusal(); return; }
        var rangeFirst = S.info.over && !S.win && !S.decoding;
        setPhase(rangeFirst ? 'range' : 'decoding');
        showState('info', t('st_answered'), t(rangeFirst ? 'st_answered_range' : 'st_answered_next'), null);
    }
    function refusedNormal() {
        var fps = S.tb0 ? S.tb0.streamFps : 0;
        return S.answer === 'normal' && fps > 0 && fps < 50;
    }
    function renderClock() {
        var tb0 = S.tb0, el = $('clClock');
        if (!tb0) return;
        show('clClock', true);
        var asking = tb0.mode === 'ask' && S.answer == null;
        var tb = S.tb || clockFor(S.answer);
        el.classList.toggle('cl-clock-ask', asking);
        $('clWork').classList.toggle('cl-asking', asking);
        var srcKey = { stream: 'clock_src_stream', tag: 'clock_src_tag', ramp: 'clock_src_ramp', user: 'clock_src_user' }[tb.source] || 'clock_src_user';
        if (asking) {
            textWrite($('clClockLine'), t('clock_q'));
            textWrite($('clClockRes'), t('clock_q_why', { fps: fmtInt(tb0.streamFps) }));
        } else if (S.answer === 'normal') {
            var nf = tb0.streamFps || tb.captureFps || 30;
            textWrite($('clClockLine'), t('clock_normal_line', { fps: fmtInt(nf) }));
            textWrite($('clClockRes'), t('clock_res', { ms: fmtMs(1000 / nf, 1) }));
        } else {
            textWrite($('clClockLine'), t('clock_line', { fps: tb.captureFps, src: t(srcKey) }));
            textWrite($('clClockRes'), t('clock_res', { ms: fmtMs(tb.frameMs, 1) }));
        }
        var open = asking || S.clockOpen || tb0.mode === 'ask';
        $('clClockAsk').hidden = !open;
        $('clClockEdit').hidden = open;
        var apple = /apple/i.test(String((S.tags || {}).make || '')) || /\.mov$/i.test(String((S.file && S.file.name) || ''));
        $('clClockIos').hidden = !(tb0.mode === 'ask' && apple);
        var cur = S.answer == null ? (tb0.mode === 'ask' ? '' : 'file') : String(S.answer);
        Array.prototype.forEach.call(document.querySelectorAll('[data-cl-fps]'), function (b) {
            var v = b.getAttribute('data-cl-fps');
            if (v === 'file') { b.hidden = tb0.mode === 'ask'; if (!b.hidden) b.textContent = t('fps_file', { fps: tb0.captureFps }); }
            b.setAttribute('aria-pressed', String(v === cur));
        });
        var note = '';
        if (refusedNormal()) note = t('clock_refuse_30', { fps: fmtInt(tb0.streamFps), ms: fmtMs(1000 / tb0.streamFps, 0) });
        else if (S.answer === 'normal') note = t('clock_note_60', { ms: fmtMs(1000 / Math.max(1, tb0.streamFps), 1) });
        else if (asking) note = t('clock_q_more');
        textWrite($('clClockNote'), note);
        $('clClockNote').hidden = !note;
    }

    /* ── After the frames: boxes, analysis, the one judge ───────────────────── */
    async function measure(gen) {
        if (!S || !S.decoded) return;
        var my = ++S.mseq;
        if (refusedNormal()) { renderRefusal(); return; }
        S.tb = clockFor(S.answer);
        if (S.tb.mode === 'ask') { renderWaitAnswer(); return; }
        S.times = S.tb.frameTimesMs;
        renderClock();
        clockWarnings();
        setPhase('analyzing');
        await ask({ type: 'times', times: S.times });
        if (gen !== S.gen || my !== S.mseq) return;
        if (!S.boxes) {
            setProgress(1, t('finding'));
            var sug = null;
            try { sug = (await ask({ type: 'suggest' })).result; } catch (e) {}
            if (gen !== S.gen || my !== S.mseq) return;
            setProgress(-1);
            S.sug = sug;
            var conf = sug ? Math.round((sug.confidence || 0) * 100) / 100 : 0;
            if (sug && sug.screen && sug.thumb && sug.confidence >= 0.5) {
                S.boxes = { screen: sug.screen, thumb: sug.thumb }; S.boxSource = 'auto';
                placeBoxes();
                await analyze(gen);
                if (gen !== S.gen || my !== S.mseq) return;
                var v = S.verdict;
                feat('cl_boxes', { conf: conf, check: v ? v.state + (v.why && v.state !== 'ok' ? ':' + v.why : '') : '', source: 'auto', moves: 0 });
                if (v && v.state === 'find-boxes') { S.boxes = null; S.boxSource = null; S.result = null; S.verdict = null; placeBoxes(); renderFindBoxes(); startPlacing(t('boxes_failed')); }
                else { textWrite($('clBoxHelp'), t('boxes_auto')); show('clBoxHelp', true); }
            } else {
                feat('cl_boxes', { conf: conf, check: 'no-suggestion', source: 'none', moves: 0 });
                renderFindBoxes(); startPlacing('');
            }
        } else await analyze(gen);
        setPhase('done');
        stage('analyzed');
    }
    function clockWarnings() {
        var tb = S.tb, warn = [];
        if (tb.captureFps && tb.captureFps >= 900) warn.push(t('warn_960'));
        if (tb.dropped && tb.dropped.length) warn.push(t('warn_dropped', { n: tb.dropped.reduce(function (a, d) { return a + d.missing; }, 0) }));
        if (S.partial && S.partial.why === 'cut') warn.push(t('warn_cut', { s: fmtMs(S.times[S.n - 1] / 1000, 1) }));
        else if (S.partial && S.partial.why === 'cancel') warn.push(t('warn_stopped', { s: fmtMs(S.times[S.n - 1] / 1000, 1) }));
        else if (S.partial) warn.push(t('warn_partial', { s: fmtMs(S.times[S.n - 1] / 1000, 1) }));
        if (S.win) warn.push(t('warn_window', { a: fmtMs((S.win.startUs - sortedPts()[0]) / 1e6, 1), s: fmtMs(S.times[S.n - 1] / 1000, 1) }));
        if (S.dup > 0.05 && tb.mode === 'index') warn.push(t('note_dup'));
        S.clockWarn = warn;
    }
    var analyzeTimer = 0, analyzing = 0;
    function scheduleAnalyze() { clearTimeout(analyzeTimer); analyzeTimer = setTimeout(function () { if (S) analyze(S.gen); }, 250); }
    async function analyze(gen) {
        if (!S || !S.times || !S.boxes || !S.boxes.thumb || !S.boxes.screen) return;
        var my = ++analyzing;
        $('clResultPanel').classList.add('cl-busy');
        try {
            var m = await ask({ type: 'analyze', boxes: S.boxes, opts: { rotationDeg: S.info.rot || 0 } });
            if (my !== analyzing || gen !== S.gen) return;
            S.result = m.result;
            renderAll();
        } catch (err) {
            if (window.__handleError) window.__handleError(err, 'camera_lag_analyze', { say: t('analyze_failed') });
        } finally { $('clResultPanel').classList.remove('cl-busy'); }
    }

    /* ── Frames ↔ milliseconds (manual marks live on the frame axis) ──────────── */
    /* A mark is kept as a FRAME position, not a time: a changed clock re-times every frame, and a
       re-analysis (a moved box) re-finds the same presses at the same frames. Marks re-attach to
       the press found within two frames of where the person made them (QA M4: a box nudge or a
       clock change erased every correction without a word). */
    function msAt(f) {
        var T2 = S.times, n = T2.length; if (!n) return 0;
        if (f <= 0) return T2[0] + f * (n > 1 ? T2[1] - T2[0] : 4);
        if (f >= n - 1) return T2[n - 1] + (f - n + 1) * (n > 1 ? T2[n - 1] - T2[n - 2] : 4);
        var i = Math.floor(f), r = f - i; return T2[i] + r * (T2[i + 1] - T2[i]);
    }
    function frameAt(ms) {
        var T2 = S.times, lo = 0, hi = T2.length - 1;
        if (ms <= T2[0]) return 0; if (ms >= T2[hi]) return hi;
        while (hi - lo > 1) { var m = (lo + hi) >> 1; if (T2[m] <= ms) lo = m; else hi = m; }
        return lo + (ms - T2[lo]) / Math.max(1e-9, T2[hi] - T2[lo]);
    }
    function markFor(i, create) {
        var r = S.result; if (!r || !r.pairs[i]) return null;
        var anchor = frameAt(r.pairs[i].press.tMs), best = null, bd = 2.01;
        S.marks.forEach(function (m) { var d = Math.abs(m.anchor - anchor); if (d < bd) { bd = d; best = m; } });
        if (!best && create) { best = { anchor: anchor, press: null, onset: null, excluded: false }; S.marks.push(best); }
        return best;
    }

    /* ── Numbers (automatic, then the person's corrections) ─────────────────── */
    function pairsNow() {
        var r = S && S.result; if (!r || !S.times) return [];
        return r.pairs.map(function (p, i) {
            var m = markFor(i, false) || {};
            var pressMs = m.press != null ? msAt(m.press) : p.press.tMs;
            var onMs = m.onset != null ? msAt(m.onset) : (p.onset ? p.onset.tMs : null);
            var lat = onMs != null ? onMs - pressMs : null;
            var order = lat != null && lat <= 0;
            return { i: i, pressMs: pressMs, onsetMs: onMs, latencyMs: lat, excluded: !!m.excluded || order, userExcluded: !!m.excluded, order: order,
                     edited: m.press != null || m.onset != null, autoLat: p.latencyMs, riseFrames: p.press.riseFrames, why: p.why };
        });
    }
    function currentStats() {
        var lat = pairsNow().filter(function (p) { return !p.excluded && p.latencyMs != null && p.latencyMs > 0; }).map(function (p) { return p.latencyMs; });
        var st = window.CameraLagDetect.stats(lat); st.values = lat; return st;
    }
    function edited() { return pairsNow().some(function (p) { return p.edited || p.userExcluded; }); }
    function verdictNow() {
        var r = S.result; if (!r || !S.tb) return null;
        var pairs = pairsNow(), eff = { pairs: pairs.filter(function (p) { return !p.userExcluded; }).map(function (p) {
            return { press: { tMs: p.pressMs, riseFrames: p.riseFrames }, latencyMs: p.order ? null : p.latencyMs };
        }), rejected: r.rejected };
        var src = S.boxSource === 'auto' && !edited() ? 'auto' : 'user';
        var v = window.CameraLagDetect.judge(eff, { source: src, boxes: S.boxes, frameMs: S.tb.frameMs, clockAsked: S.tb.source === 'user' });
        if (v.show && S.answer === 'normal' && S.tb.frameMs > 12) v.coarse = true;
        return v;
    }

    /* ── Render ─────────────────────────────────────────────────────────────── */
    function renderAll() {
        S.verdict = verdictNow();
        renderResult(); renderPresses(); drawTrace(); drawView(); renderCompare(); renderExport();
        if (RV) renderReview();
        reportResult();
    }
    function reportResult() {
        var v = S.verdict; if (!v || !S.result) return;
        var st = currentStats(), key = v.show ? verdictKey(st.median) : v.state;
        var payload = { presses: S.result.pairs.length, n_lat: st.n, median: v.show ? round1(st.median) : null, iqr: v.show && st.n ? round1(st.q3 - st.q1) : null,
                        verdict_key: key, provisional: v.provisional ? 1 : 0, src: S.boxSource || '', fps: S.tb.captureFps, clock: S.tb.source };
        S.lastResult = payload;
        if (!S.reported.first) { S.reported.first = true; feat('cl_result', Object.assign({ kind: 'first' }, payload)); }
        if (v.show && S.autoMedian == null && !edited()) S.autoMedian = st.median;
        if (v.show && !v.provisional && !S.reported.complete) {
            S.reported.complete = true;
            try { if (window.__aeToolComplete) window.__aeToolComplete({ n: st.n, median: Math.round(st.median), fps: S.tb.captureFps, src: S.tb.source, lane: S.lane, auto: S.boxSource === 'auto' ? 1 : 0 }); } catch (e) {}
        }
    }
    /* What the person last saw for this file, and how far their own corrections moved it —
       said once, when the file is left (another file, the page closed). */
    function finishFile(why) {
        if (!S || !S.result || !S.times || S.reported.final) return;
        S.reported.final = true;
        if (S.lastResult) feat('cl_result', Object.assign({ kind: 'final', why: why }, S.lastResult));
        var pairs = pairsNow(), ed = pairs.filter(function (p) { return p.edited; }).length, ex = pairs.filter(function (p) { return p.userExcluded; }).length;
        if (ed || ex) feat('cl_edits', { edited: ed, excluded: ex, median_auto: round1(S.autoMedian), median_final: round1(currentStats().median) });
    }
    /* Bands that agree with the reference table under them: a 60 fps game ≈ 40–100 ms, a 30 fps
       game ≈ 100–170 ms (QA m3: PS5 at 30 fps ≈ 153 ms was called «slow — turn on game mode»). */
    function verdictKey(med) { return med < 50 ? 'v_fast' : med < 100 ? 'v_good' : med < 170 ? 'v_ok' : 'v_slow'; }
    function renderResult() {
        var v = S.verdict, st = currentStats(), pairs = pairsNow(), r = S.result;
        show('clResultPanel', true);
        if (!v || !v.show) { renderNoNumber(v); return; }
        S.stateShown = null;
        $('clResultPanel').classList.remove('cl-none-state');
        show('clNumber', true); show('clState', false);
        var notes = [];
        textWrite($('clBig'), fmtMs(st.median, 0));
        $('clProv').hidden = !v.provisional;
        textWrite($('clSpread'), st.n >= 2 ? t('spread', { lo: fmtMs(st.q1, 0), hi: fmtMs(st.q3, 0), n: st.n, min: fmtMs(st.min, 0), max: fmtMs(st.max, 0) }) : t('spread_one', { n: st.n }));
        textWrite($('clPrecision'), t(v.coarse ? 'precision_coarse' : 'precision', { ms: fmtMs(S.tb.frameMs, 1) }));
        var key = verdictKey(st.median);
        showVerdict(key === 'v_slow' ? 'warn' : 'ok', t(key, { ms: fmtMs(st.median, 0) }), t(key + '_more'));
        if (pairs.length < 8) notes.push(t('note_few', { n: pairs.length }));
        var missing = pairs.filter(function (p) { return p.latencyMs == null; }).length;
        if (missing) notes.push(t('note_missing', { n: missing }));
        var bad = pairs.filter(function (p) { return p.order && !p.userExcluded; }).length;
        if (bad) notes.push(t('note_order', { n: bad }));
        if (st.outliers && st.outliers.length) notes.push(t('note_outliers', { n: st.outliers.length }));
        if (v.coarse) notes.unshift(t('note_coarse', { ms: fmtMs(S.tb.frameMs, 0) }));
        /* The game's frame rate from the picture — only when the camera could see it: a rhythm at
           more than half the capture rate aliases (QA m4). */
        var cad = r && r.cadence, cadOk = !!(cad && cad.reliable && cad.gameFps <= S.tb.captureFps / 2 &&
            [30, 40, 60, 120].some(function (g) { return Math.abs(cad.gameFps - g) / g < 0.1; }));   // the bench's 60 fps game read as «20»: a sub-harmonic is not a rate
        textWrite($('clGame'), cadOk ? t('game_fps', { fps: Math.round(cad.gameFps) }) : '');
        $('clGame').hidden = !cadOk;
        var box = S.boxes.screen, mid = (box.y + box.h / 2) / S.ah;
        textWrite($('clHeight'), t(mid < 0.36 ? 'measured_top' : mid > 0.64 ? 'measured_bottom' : 'measured_mid'));
        fillNotes((S.clockWarn || []).concat(notes));
    }
    function fillNotes(list) {
        var ul = $('clNotes'); ul.textContent = '';
        list.forEach(function (n) { var li = document.createElement('li'); li.textContent = n; ul.appendChild(li); });
        ul.hidden = !ul.childNodes.length;
    }
    /* No number: say WHY, and the one thing to do next. */
    function renderNoNumber(v) {
        var st = v ? v.state : 'no-presses', pairs = pairsNow();
        var map = {
            'no-presses': ['st_no_presses', 'st_no_presses_next', 'place'],
            'no-change': ['st_no_change', 'st_no_change_next', 'review'],
            'scattered': ['st_scattered', 'st_scattered_next', 'review'],
            'implausible': ['st_implausible', 'st_implausible_next', 'review'],
            'clock-fast': ['st_clock_fast', 'st_clock_fast_next', 'clock'],
            'clock-slow': ['st_clock_slow', 'st_clock_slow_next', 'clock'],
            'find-boxes': ['st_find_boxes', 'st_find_boxes_next', 'place']
        }[st] || ['st_no_presses', 'st_no_presses_next', 'place'];
        var r = { n: pairs.length, ms: v && v.riseMs != null ? fmtMs(v.riseMs, 0) : '—', fps: S.tb ? S.tb.captureFps : '' };
        showState('warn', t(map[0], r), t(map[1], r), map[2]);
        // the answer to a clock doubt is in the clock panel: open its choices and bring it up
        if (map[2] === 'clock') { S.clockOpen = true; renderClock(); $('clWork').classList.add('cl-asking'); }
        fillNotes(S.clockWarn || []);
    }
    function showState(kind, head, more, action) {
        show('clResultPanel', true);
        $('clResultPanel').classList.add('cl-none-state');
        show('clNumber', false); show('clState', true);
        $('clState').className = 'cl-state cl-v-' + kind;
        textWrite($('clStateHead'), head); textWrite($('clStateMore'), more || '');
        $('clStatePlace').hidden = action !== 'place' || !(S && S.decoded);
        $('clStateReview').hidden = action !== 'review' || !(S && S.result && S.result.pairs.length);
        $('clStateRepair').hidden = action !== 'repair';
        $('clStateRetry').hidden = action !== 'retry';
        $('clStateGuide').hidden = !(kind === 'refuse' || action === 'clock' || (action === 'place' && S && S.verdict && S.verdict.state === 'no-presses'));
        if (S) S.stateShown = kind;
        if (kind !== 'warn') fillNotes([]);
        renderExport();
    }
    function renderWaitAnswer() {
        S.verdict = null;
        setPhase('ask');
        showState('ask', t('st_ask'), t('st_ask_next'), null);
        renderClock();
    }
    function renderRefusal() {
        S.verdict = null; S.phase = 'refused'; $('clWork').setAttribute('data-phase', 'refused');
        showState('refuse', t('st_refuse_30', { fps: fmtInt(S.tb0.streamFps) }), t('st_refuse_30_next', { ms: fmtMs(1000 / S.tb0.streamFps, 0) }), null);
        renderClock();
        ['clPressPanel', 'clTracePanel', 'clReview', 'clComparePanel', 'clBoxHelp'].forEach(function (id) { show(id, false); });
        RV = null;
        feat('cl_refused', { why: 'normal_' + Math.round(S.tb0.streamFps) });
    }
    function renderFindBoxes() {
        showState('warn', t('st_find_boxes'), t('st_find_boxes_next'), 'place');
        ['clPressPanel', 'clTracePanel', 'clComparePanel', 'clReview'].forEach(function (id) { show(id, false); });
        RV = null;
    }
    function showVerdict(kind, head, more) {
        $('clVerdict').className = 'cl-verdict cl-v-' + kind;
        textWrite($('clVerdictHead'), head); textWrite($('clVerdictMore'), more || '');
    }
    function renderPresses() {
        var pairs = pairsNow(), ul = $('clPresses'); ul.textContent = '';
        show('clPressPanel', pairs.length > 0);
        var mx = Math.max.apply(null, pairs.map(function (p) { return p.latencyMs > 0 ? p.latencyMs : 0; }).concat([1]));
        pairs.forEach(function (p) {
            var li = document.createElement('li'); li.className = 'cl-press' + (p.excluded ? ' cl-off' : '') + (p.latencyMs == null ? ' cl-none' : '') + (p.order ? ' cl-bad' : '');
            var b = document.createElement('button'); b.type = 'button'; b.className = 'cl-press-b';
            b.setAttribute('aria-label', t('review_press', { n: p.i + 1 }));
            var num = document.createElement('span'); num.className = 'cl-press-n'; num.textContent = String(p.i + 1);
            var bar = document.createElement('span'); bar.className = 'cl-press-bar';
            var fill = document.createElement('i'); fill.style.width = p.latencyMs > 0 ? Math.max(4, 100 * p.latencyMs / mx) + '%' : '0';
            bar.appendChild(fill);
            var val = document.createElement('span'); val.className = 'cl-press-v';
            val.textContent = p.order ? t('order_short') : p.latencyMs == null ? t('no_change_short') : fmtMs(p.latencyMs, 0) + ' ' + t('ms');
            if (p.edited) { var ed = document.createElement('span'); ed.className = 'cl-press-ed'; ed.textContent = t('edited'); val.appendChild(ed); }
            b.appendChild(num); b.appendChild(bar); b.appendChild(val);
            b.addEventListener('click', function () { openReview(p.i); });
            li.appendChild(b); ul.appendChild(li);
        });
    }

    /* ── The frame view with two boxes ─────────────────────────────────────── */
    var viewSnap = 0;
    function setupView() {
        var range = $('clScrub');
        range.min = '0'; range.max = String(Math.max(0, S.snaps.length - 1)); range.step = '1';
        viewSnap = Math.floor(S.snaps.length / 2); range.value = String(viewSnap);
        show('clView', true); show('clScrubWrap', S.snaps.length > 1); show('clBoxBar', true);
        drawView(); placeBoxes();
    }
    function drawView() {
        var cv = $('clFrame'); if (!S || !cv || !S.snaps.length) return;
        var snap = S.snaps[Math.max(0, Math.min(S.snaps.length - 1, viewSnap))];
        var src = snap && snap.bmp, w = (src && src.width) || S.aw * 4, h = (src && src.height) || S.ah * 4;
        if (cv.width !== w) cv.width = w; if (cv.height !== h) cv.height = h;
        $('clView').style.aspectRatio = w + ' / ' + h;
        var ctx = cv.getContext('2d');
        if (src) ctx.drawImage(src, 0, 0, w, h); else { ctx.fillStyle = '#222'; ctx.fillRect(0, 0, w, h); }
        // the label is THIS picture's time, not the scrubber's (QA m8: the picture was up to 110 ms away)
        var ms = S.times ? S.times[Math.min(S.n - 1, snap.k)] : (S.framePts[snap.k] - S.framePts[0]) / 1000;
        textWrite($('clScrubT'), fmtMs(ms / 1000, 2) + ' ' + t('s'));
        placeBoxes();
    }
    function placeBoxes() {
        ['thumb', 'screen'].forEach(function (k) {
            var el = $(k === 'thumb' ? 'clBoxThumb' : 'clBoxScreen');
            var b = S && S.boxes && S.boxes[k];
            if (!b) { el.hidden = true; return; }
            el.style.left = (100 * b.x / S.aw) + '%'; el.style.top = (100 * b.y / S.ah) + '%';
            el.style.width = (100 * b.w / S.aw) + '%'; el.style.height = (100 * b.h / S.ah) + '%';
            // a box at the far edge keeps its label inside the frame
            el.classList.toggle('cl-tag-end', (rtl() ? (S.aw - b.x - b.w) : b.x) / S.aw > 0.55);
            el.hidden = false;
        });
    }
    function boxesChanged(how) {
        S.boxSource = how; S.boxMoves++;
        // a key held on a box moves it a pixel per repeat: one event per move would flood the log
        if (S.boxMoves === 1 || S.boxMoves % 10 === 0) feat('cl_boxes', { conf: S.sug ? Math.round((S.sug.confidence || 0) * 100) / 100 : 0, check: 'moved', source: how, moves: S.boxMoves });
        scheduleAnalyze();
    }
    function bindBoxes() {
        ['clBoxThumb', 'clBoxScreen'].forEach(function (id) {
            var el = $(id), key = id === 'clBoxThumb' ? 'thumb' : 'screen';
            el.addEventListener('pointerdown', function (e) {
                if (!S || !S.boxes || !S.boxes[key] || S.placing) return;
                e.preventDefault();
                var resize = e.target.classList.contains('cl-h'), rect = $('clView').getBoundingClientRect();
                var start = { x: e.clientX, y: e.clientY, b: Object.assign({}, S.boxes[key]) }, moved = false;
                try { el.setPointerCapture(e.pointerId); } catch (x) {}
                function mv(ev) {
                    // a drag starts past a few pixels: a finger that only touched does not move the box
                    if (!moved && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < (ev.pointerType === 'touch' ? 6 : 3)) return;
                    moved = true;
                    var dx = (ev.clientX - start.x) / rect.width * S.aw, dy = (ev.clientY - start.y) / rect.height * S.ah, b = start.b;
                    var nb = resize ? { x: b.x, y: b.y, w: Math.max(4, b.w + dx), h: Math.max(4, b.h + dy) } : { x: b.x + dx, y: b.y + dy, w: b.w, h: b.h };
                    nb.x = Math.max(0, Math.min(S.aw - nb.w, nb.x)); nb.y = Math.max(0, Math.min(S.ah - nb.h, nb.y));
                    nb.w = Math.min(nb.w, S.aw - nb.x); nb.h = Math.min(nb.h, S.ah - nb.y);
                    S.boxes[key] = nb; placeBoxes();
                }
                function end() { el.removeEventListener('pointermove', mv); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', cancel); }
                function up() {
                    end();
                    if (!moved) return;
                    S.boxes[key] = { x: Math.round(S.boxes[key].x), y: Math.round(S.boxes[key].y), w: Math.round(S.boxes[key].w), h: Math.round(S.boxes[key].h) };
                    boxesChanged('drag');
                }
                function cancel() { end(); if (moved) { S.boxes[key] = start.b; placeBoxes(); } }
                el.addEventListener('pointermove', mv); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', cancel);
            });
            // keyboard: arrows move, Shift+arrows resize — the box owns these keys, not the review
            el.addEventListener('keydown', function (e) {
                if (!S || !S.boxes || !S.boxes[key]) return;
                var d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key]; if (!d) return;
                e.preventDefault(); e.stopPropagation();
                var b = S.boxes[key];
                if (e.shiftKey) { b.w = Math.max(4, Math.min(S.aw - b.x, b.w + d[0])); b.h = Math.max(4, Math.min(S.ah - b.y, b.h + d[1])); }
                else { b.x = Math.max(0, Math.min(S.aw - b.w, b.x + d[0])); b.y = Math.max(0, Math.min(S.ah - b.h, b.y + d[1])); }
                placeBoxes(); boxesChanged('keys');
            });
        });
        $('clScrub').addEventListener('input', function () { viewSnap = +this.value; drawView(); });
        $('clView').addEventListener('click', onViewTap);
        $('clPlace').addEventListener('click', function () { startPlacing(''); });
        $('clStatePlace').addEventListener('click', function () { startPlacing(''); try { $('clView').scrollIntoView({ block: 'nearest' }); } catch (e) {} });
    }

    /* Two taps instead of two drags: one on the thumb, one on the screen. The boxes grow
       around the taps (sizes in proportion to the frame) and can still be dragged after. */
    function startPlacing(msg) {
        if (!S || !S.decoded) return;
        S.placing = 'thumb';
        $('clView').classList.add('cl-placing');
        textWrite($('clBoxHelp'), (msg ? msg + ' ' : '') + t('place_thumb'));
        show('clBoxHelp', true);
        feat('place_start', { auto: msg ? 1 : 0 });
    }
    function onViewTap(e) {
        if (!S || !S.placing) return;
        var rect = $('clView').getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width * S.aw, y = (e.clientY - rect.top) / rect.height * S.ah;
        S.boxes = S.boxes || {};
        if (S.placing === 'thumb') {
            var tw = Math.round(S.aw * 0.13), th = Math.round(S.ah * 0.3);
            S.boxes.thumb = window.CameraLagDetect.clampBox({ x: x - tw / 2, y: y - th / 2, w: tw, h: th }, S.aw, S.ah);
            S.placing = 'screen'; textWrite($('clBoxHelp'), t('place_screen'));
            placeBoxes();
        } else {
            var sw = Math.round(S.aw * 0.3), sh = Math.round(S.ah * 0.3);
            S.boxes.screen = window.CameraLagDetect.clampBox({ x: x - sw / 2, y: y - sh / 2, w: sw, h: sh }, S.aw, S.ah);
            S.placing = null; $('clView').classList.remove('cl-placing'); textWrite($('clBoxHelp'), t('boxes_manual'));
            placeBoxes();
            feat('place_done');
            boxesChanged('tap');
        }
    }

    /* ── The trace: hand and screen on one time axis ───────────────────────── */
    function cssVar(n, d) { try { return getComputedStyle($('clWork')).getPropertyValue(n).trim() || d; } catch (e) { return d; } }
    function drawTrace() {
        var r = S && S.result; if (!r || !S.times) return;
        var vis = r.pairs.length > 0;
        show('clTracePanel', vis); if (!vis) return;
        var cv = $('clTrace'), dpr = window.devicePixelRatio || 1, W = cv.clientWidth || 600, H = cv.clientHeight || 150;
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
        var ctx = cv.getContext('2d'); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
        var amber = cssVar('--cl-hand', '#f2a93b'), teal = cssVar('--cl-screen', '#3fc5e8'), muted = cssVar('--cl-muted', '#888');
        var T0 = S.times[0], T1 = S.times[S.n - 1];
        var x = function (ms) { return 8 + (W - 16) * (ms - T0) / Math.max(1, T1 - T0); };
        var p = r.pressSignal, mx = 0, mn = Infinity; for (var i = 0; i < p.length; i++) { if (p[i] > mx) mx = p[i]; if (p[i] < mn) mn = p[i]; }
        ctx.strokeStyle = amber; ctx.lineWidth = 1.5; ctx.beginPath();
        for (var k = 0; k < p.length; k++) { var yy = 10 + (H * 0.42) * (1 - (p[k] - mn) / Math.max(1e-6, mx - mn)); if (k) ctx.lineTo(x(S.times[k]), yy); else ctx.moveTo(x(S.times[k]), yy); }
        ctx.stroke();
        var pairs = pairsNow();
        pairs.forEach(function (pp) {
            var px = x(pp.pressMs);
            ctx.globalAlpha = pp.excluded ? 0.3 : 1;
            if (pp.onsetMs != null) {
                var ox = x(pp.onsetMs);
                ctx.fillStyle = muted; ctx.globalAlpha *= 0.18; ctx.fillRect(Math.min(px, ox), H * 0.55, Math.max(1, Math.abs(ox - px)), H * 0.4); ctx.globalAlpha = pp.excluded ? 0.3 : 1;
                ctx.fillStyle = teal; ctx.fillRect(ox - 1, H * 0.5, 2, H * 0.46);
            }
            ctx.fillStyle = amber; ctx.fillRect(px - 1, 6, 2, H * 0.9);
            ctx.fillStyle = muted; ctx.font = '12px system-ui, sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(String(pp.i + 1), px, H - 3);
        });
        ctx.globalAlpha = 1;
        cv.onclick = function (e) {
            var rect = cv.getBoundingClientRect(), mxp = e.clientX - rect.left, best = -1, bd = 24;
            pairs.forEach(function (pp) { var d = Math.abs(x(pp.pressMs) - mxp); if (d < bd) { bd = d; best = pp.i; } });
            if (best >= 0) openReview(best);
        };
    }

    /* ── Review one press: frame by frame, full-resolution crops ───────────── */
    var RV = null;
    async function openReview(i) {
        var pairs = pairsNow(), p = pairs[i]; if (!p || !S.times) return;
        show('clReview', true);
        textWrite($('clReviewTitle'), t('review_title', { n: i + 1 }));
        var pf = window.CameraLagDetect.nearestFrame(S.times, p.pressMs);
        var of = p.onsetMs != null ? window.CameraLagDetect.nearestFrame(S.times, p.onsetMs) : Math.min(S.n - 1, pf + Math.round(250 / S.tb.frameMs));
        var from = Math.max(0, Math.min(pf, of) - 8), to = Math.min(S.n - 1, Math.max(pf, of) + 10);
        var rv = RV = { i: i, from: from, to: to, cur: pf, press: pf, onset: p.onsetMs != null ? of : -1, crops: {}, loading: true };
        renderReview();
        try { $('clReview').scrollIntoView({ block: 'nearest' }); } catch (e) {}
        try { await loadCrops(rv); } catch (err) { if (window.__handleError) window.__handleError(err, 'camera_lag_review', { say: t('rv_failed') }); }
        if (RV !== rv) return;
        rv.loading = false; renderReview();
        feat('review_open');
    }
    async function loadCrops(rv) {
        var gen = S.gen, V = window.VideoMergeWebCodecs;
        var tb = S.boxes.thumb, sb = S.boxes.screen;
        if (S.lane === 'webcodecs' && S.info.d && V) {
            try {
                var startUs = S.framePts[rv.from], endUs = S.framePts[rv.to];
                var st = new V.DecodeStream(S.info.d, startUs, endUs, { hwm: 4 });
                var idx = {}; for (var k = rv.from; k <= rv.to; k++) idx[S.framePts[k]] = k;
                var scale = cropScale(tb, sb);
                for (;;) {
                    var f = await st.next(); if (!f) break;
                    var kk = idx[f.timestamp];
                    if (kk != null && !rv.crops[kk]) rv.crops[kk] = { thumb: cropOf(f, tb, scale), screen: cropOf(f, sb, scale) };
                    var past = f.timestamp > endUs; f.close();
                    if (past || gen !== S.gen || RV !== rv) break;
                }
                st.close();
                if (Object.keys(rv.crops).length) return;
            } catch (e) {}
        }
        var m = await ask({ type: 'crops', from: rv.from, to: rv.to, boxes: S.boxes });
        m.frames.forEach(function (fr) { rv.crops[fr.k] = { thumb: grayBitmap(fr.thumb, m.tb.w, m.tb.h), screen: grayBitmap(fr.screen, m.sb.w, m.sb.h) }; });
    }
    /* Both crops at ONE scale, so a thumb and a screen keep their real proportions next to each
       other (QA: the thumb came out magnified against the screen). */
    function cropScale(tb, sb) {
        var fw = S.fullW / S.aw, need = Math.max(tb.w, tb.h, sb.w, sb.h) * fw;
        // a low-resolution clip is magnified (up to 2.5×) so a frame step is visible at all
        return Math.min(2.5, 560 / Math.max(1, need));
    }
    function cropOf(f, b, sc) {
        var w = f.displayWidth || f.codedWidth, h = f.displayHeight || f.codedHeight;
        var sx = Math.round(b.x / S.aw * w), sy = Math.round(b.y / S.ah * h), sw = Math.round(b.w / S.aw * w), sh = Math.round(b.h / S.ah * h);
        var c = document.createElement('canvas'); c.width = Math.max(1, Math.round(sw * sc)); c.height = Math.max(1, Math.round(sh * sc));
        c.getContext('2d').drawImage(f, sx, sy, sw, sh, 0, 0, c.width, c.height);
        return c;
    }
    function grayBitmap(arr, w, h) {
        var c = document.createElement('canvas'); c.width = w; c.height = h; var x = c.getContext('2d'), id = x.createImageData(w, h);
        for (var i = 0; i < w * h; i++) { id.data[i * 4] = id.data[i * 4 + 1] = id.data[i * 4 + 2] = arr[i]; id.data[i * 4 + 3] = 255; }
        x.putImageData(id, 0, 0); return c;
    }
    function renderReview() {
        var rv = RV; if (!rv || !S || !S.result) return;
        var c = rv.crops[rv.cur];
        [['clRvThumb', 'thumb'], ['clRvScreen', 'screen']].forEach(function (pair) {
            var cv = $(pair[0]), src = c && c[pair[1]];
            if (!src) { cv.width = 4; cv.height = 3; return; }
            cv.width = src.width; cv.height = src.height;
            var x = cv.getContext('2d'); x.imageSmoothingEnabled = src.width > 120; x.drawImage(src, 0, 0);
        });
        var dt = S.times[rv.cur] - S.times[rv.press];
        textWrite($('clRvPos'), t('rv_pos', { k: (rv.cur - rv.press >= 0 ? '+' : '') + (rv.cur - rv.press), ms: (dt >= 0 ? '+' : '') + fmtMs(dt, 1) }));
        $('clRvLoading').hidden = !rv.loading;
        var strip = $('clRvStrip'); strip.textContent = '';
        for (var k = rv.from; k <= rv.to; k++) {
            var b = document.createElement('button'); b.type = 'button'; b.className = 'cl-rv-cell' + (k === rv.cur ? ' on' : '') + (k === rv.press ? ' is-press' : '') + (k === rv.onset ? ' is-onset' : '');
            b.setAttribute('aria-label', t('rv_frame', { k: k - rv.press }));
            (function (kk) { b.addEventListener('click', function () { if (RV) { RV.cur = kk; renderReview(); } }); })(k);
            var cc = rv.crops[k]; if (cc && cc.screen) { var mini = document.createElement('canvas'); mini.width = 48; mini.height = Math.max(1, Math.round(48 * cc.screen.height / cc.screen.width)); mini.getContext('2d').drawImage(cc.screen, 0, 0, mini.width, mini.height); b.appendChild(mini); }
            strip.appendChild(b);
        }
        var p = pairsNow()[rv.i];
        if (!p) return;
        textWrite($('clRvLat'), p.order ? t('rv_order') : p.latencyMs != null ? t('rv_latency', { ms: fmtMs(p.latencyMs, 1) }) : t('no_change_short'));
        textWrite($('clRvExclude'), p.userExcluded ? t('rv_include') : t('rv_exclude'));
        $('clRvReset').hidden = !(p.edited || p.userExcluded);
        try { var on = strip.querySelector('.on'); if (on) strip.scrollLeft = on.offsetLeft - strip.clientWidth / 2; } catch (e) {}
    }
    function bindReview() {
        $('clRvPrev').addEventListener('click', function () { stepReview(-1); });
        $('clRvNext').addEventListener('click', function () { stepReview(1); });
        $('clRvSetPress').addEventListener('click', function () { markReview('press'); });
        $('clRvSetOnset').addEventListener('click', function () { markReview('onset'); });
        $('clRvExclude').addEventListener('click', function () {
            if (!RV) return; var m = markFor(RV.i, true); m.excluded = !m.excluded;
            feat('press_excluded', { v: m.excluded ? 1 : 0 }); afterEdit();
        });
        $('clRvReset').addEventListener('click', function () {
            if (!RV) return; var m = markFor(RV.i, false);
            if (m) S.marks.splice(S.marks.indexOf(m), 1);
            var p = S.result.pairs[RV.i];
            RV.press = window.CameraLagDetect.nearestFrame(S.times, p.press.tMs);
            RV.onset = p.onset ? window.CameraLagDetect.nearestFrame(S.times, p.onset.tMs) : -1;
            feat('manual_reset'); afterEdit();
        });
        $('clRvClose').addEventListener('click', function () { show('clReview', false); RV = null; });
        $('clRvPrevPress').addEventListener('click', function () { if (RV && RV.i > 0) openReview(RV.i - 1); });
        $('clRvNextPress').addEventListener('click', function () { if (RV && RV.i + 1 < pairsNow().length) openReview(RV.i + 1); });
        document.addEventListener('keydown', function (e) {
            if (!RV || $('clReview').hidden || e.defaultPrevented) return;
            if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
            if (e.target && e.target.closest && e.target.closest('.cl-box, input, select, textarea, [contenteditable]')) return;
            var back = rtl() ? 'ArrowRight' : 'ArrowLeft', fwd = rtl() ? 'ArrowLeft' : 'ArrowRight';
            if (e.key === back) { e.preventDefault(); stepReview(e.shiftKey ? -10 : -1); }
            else if (e.key === fwd) { e.preventDefault(); stepReview(e.shiftKey ? 10 : 1); }
        });
    }
    function stepReview(d) {
        var rv = RV; if (!rv) return;
        var nk = rv.cur + d;
        if (nk < rv.from || nk > rv.to) {
            rv.from = Math.max(0, Math.min(rv.from, nk - 4)); rv.to = Math.min(S.n - 1, Math.max(rv.to, nk + 4));
            rv.loading = true;
            // the review may be closed or moved to another press before the frames arrive (QA M8)
            loadCrops(rv).then(function () { if (RV !== rv) return; rv.loading = false; renderReview(); })
                .catch(function (err) { if (RV === rv) { rv.loading = false; renderReview(); } if (window.__handleError) window.__handleError(err, 'camera_lag_review', { say: t('rv_failed') }); });
        }
        rv.cur = Math.max(0, Math.min(S.n - 1, nk)); renderReview();
    }
    function markReview(field) {
        if (!RV) return;
        var m = markFor(RV.i, true);
        // the chosen frame is the first frame that SHOWS it: the moment lies in the gap before it
        m[field] = field === 'onset' ? RV.cur - 0.5 : RV.cur;
        if (field === 'press') RV.press = RV.cur; else RV.onset = RV.cur;
        feat('manual_mark', { what: field });
        afterEdit();
    }
    function afterEdit() { S.verdict = verdictNow(); renderResult(); renderPresses(); drawTrace(); renderReview(); renderCompare(); renderExport(); reportResult(); }

    /* ── Saved setups and comparison ───────────────────────────────────────── */
    var RUNS_KEY = 'camera-lag-test:runs', PAIR_KEY = 'camera-lag-test:pair';
    var PAIRS = { game: ['lbl_game_on', 'lbl_game_off'], link: ['lbl_wired', 'lbl_bt'], hz: ['lbl_60hz', 'lbl_120hz'] };
    function okToSave() { return !!(S && S.verdict && S.verdict.show && S.stateShown == null); }
    function renderCompare() {
        var st = currentStats(), ok = okToSave(), runs = lsGet(RUNS_KEY, []);
        show('clComparePanel', ok || runs.length > 1);
        var a = $('clCmpA'), b = $('clCmpB');
        [a, b].forEach(function (sel, si) {
            var keep = sel.value; sel.textContent = '';
            if (ok) { var cur = document.createElement('option'); cur.value = 'now'; cur.textContent = t('cmp_now', { ms: fmtMs(st.median, 0) }); sel.appendChild(cur); }
            runs.forEach(function (r) { var o = document.createElement('option'); o.value = r.id; o.textContent = r.label + ' — ' + fmtMs(r.median, 0) + ' ' + t('ms'); sel.appendChild(o); });
            var dflt = si === 0 ? (runs.length ? runs[runs.length - 1].id : 'now') : (ok ? 'now' : (runs.length > 1 ? runs[runs.length - 2].id : ''));
            sel.value = keep && ((keep === 'now' && ok) || runs.some(function (r) { return r.id === keep; })) ? keep : dflt;
        });
        $('clCmpWrap').hidden = runs.length + (ok ? 1 : 0) < 2;
        textWrite($('clSavedList'), runs.length ? t('saved_n', { n: runs.length }) : t('saved_none'));
        $('clSave').disabled = !ok; $('clSaveA').disabled = !ok; $('clSaveB').disabled = !ok;
        $('clClear').hidden = !runs.length;
        renderPair();
    }
    function renderPair() {
        var pair = lsGet(PAIR_KEY, null), keys = pair && PAIRS[pair.kind];
        Array.prototype.forEach.call(document.querySelectorAll('[data-cl-pair]'), function (b) {
            var k = b.getAttribute('data-cl-pair');
            b.setAttribute('aria-pressed', String(keys ? k === pair.kind : k === 'custom'));
        });
        $('clPairSteps').hidden = !keys;
        $('clCustom').hidden = !!keys;
        if (!keys) return;
        var runs = lsGet(RUNS_KEY, []), byId = function (id) { return runs.filter(function (r) { return r.id === id; })[0]; };
        ['A', 'B'].forEach(function (side, si) {
            var run = byId(pair[side]), btn = $('clSave' + side), lab = t(keys[si]);
            btn.textContent = run ? t('pair_saved', { label: lab, ms: fmtMs(run.median, 0) }) : t('pair_save_as', { label: lab });
            btn.classList.toggle('cl-done', !!run);
        });
        var a = byId(pair.A), b = byId(pair.B);
        textWrite($('clPairHint'), a && b ? t('pair_done') : (a || b) ? t('pair_next', { label: t(keys[a ? 1 : 0]) }) : t('pair_first'));
    }
    function runById(id) {
        if (id === 'now') { var st = currentStats(); return { label: $('clLabel').value.trim() || t('cmp_this'), lat: st.values, median: st.median }; }
        return lsGet(RUNS_KEY, []).filter(function (r) { return r.id === id; })[0];
    }
    /* Only when the person asks (a select changed, a pair completed) — rendering alone fired
       «compare» three times per file (QA telemetry). A difference is claimed only when the 95 %
       interval excludes zero; when the interval is wider than the difference itself, the answer
       is «too few presses», not «no difference» (QA M9: 448 ms «no difference» at n = 4). */
    function compareNow(userAction) {
        var A = runById($('clCmpA').value), B = runById($('clCmpB').value), out = $('clCmpOut');
        if (!A || !B || $('clCmpA').value === $('clCmpB').value || !A.lat || !B.lat || !A.lat.length || !B.lat.length) { out.textContent = ''; return; }
        var bd = window.CameraLagDetect.bootstrapDiff(A.lat, B.lat, 4000, 99);
        var faster = bd.diff > 0 ? A : B, d = Math.abs(bd.diff), lo = Math.min(Math.abs(bd.lo), Math.abs(bd.hi)), hi = Math.max(Math.abs(bd.lo), Math.abs(bd.hi));
        var few = Math.min(A.lat.length, B.lat.length) < 5;
        var sure = !few && ((bd.lo > 0 && bd.hi > 0) || (bd.lo < 0 && bd.hi < 0));
        var half = (bd.hi - bd.lo) / 2;
        var kind = sure ? 'faster' : (few || half > Math.max(d, 8)) ? 'unsure' : 'same';
        out.textContent = kind === 'faster' ? t('cmp_faster', { a: faster.label, ms: fmtMs(d, 0), lo: fmtMs(lo, 0), hi: fmtMs(hi, 0) })
                        : kind === 'unsure' ? t('cmp_unsure', { ms: fmtMs(d, 0), ci: fmtMs(half, 0) })
                        : t('cmp_same', { ms: fmtMs(Math.max(Math.abs(bd.lo), Math.abs(bd.hi)), 0) });
        out.className = 'cl-cmp-out' + (sure ? ' sure' : '');
        if (userAction) feat('cl_compare', { sure: sure ? 1 : 0, kind: kind, n_a: A.lat.length, n_b: B.lat.length, diff: round1(bd.diff), ci: round1(half) });
    }
    function saveRun(label) {
        if (!okToSave()) return null;
        var st = currentStats();
        label = label || $('clLabel').value.trim() || t('cmp_untitled', { n: lsGet(RUNS_KEY, []).length + 1 });
        var runs = lsGet(RUNS_KEY, []);
        var run = { id: 'r' + Date.now().toString(36), label: label.slice(0, 60), date: Date.now(), median: st.median, n: st.n, lat: st.values.map(function (v) { return Math.round(v * 10) / 10; }), fps: S.tb.captureFps, provisional: S.verdict.provisional ? 1 : 0 };
        runs.push(run);
        while (runs.length > 12) runs.shift();
        lsSet(RUNS_KEY, runs);
        $('clLabel').value = '';
        if (window.__toast) window.__toast(t('saved_toast', { label: label }), 'success');
        feat('run_saved', { n: runs.length });
        return run;
    }
    function savePairSide(side) {
        var pair = lsGet(PAIR_KEY, null); if (!pair || !PAIRS[pair.kind]) return;
        var run = saveRun(t(PAIRS[pair.kind][side === 'A' ? 0 : 1])); if (!run) return;
        pair[side] = run.id; lsSet(PAIR_KEY, pair);
        renderCompare();
        var other = pair[side === 'A' ? 'B' : 'A'];
        if (other && lsGet(RUNS_KEY, []).some(function (r) { return r.id === other; })) {
            $('clCmpA').value = pair.A; $('clCmpB').value = pair.B; compareNow(true);
        }
    }
    var clearArmed = 0;
    function clearRuns() {
        var btn = $('clClear'), n = lsGet(RUNS_KEY, []).length;
        // two presses: the saved runs of earlier sessions are gone for good (QA m2)
        if (Date.now() - clearArmed > 4000) {
            clearArmed = Date.now(); btn.textContent = t('clear_confirm', { n: n }); btn.classList.add('cl-armed');
            setTimeout(function () { if (clearArmed && Date.now() - clearArmed >= 3900) { clearArmed = 0; btn.textContent = t('clear_runs'); btn.classList.remove('cl-armed'); } }, 4000);
            return;
        }
        clearArmed = 0; btn.textContent = t('clear_runs'); btn.classList.remove('cl-armed');
        lsSet(RUNS_KEY, []); lsSet(PAIR_KEY, null); $('clCmpOut').textContent = '';
        feat('runs_cleared', { n: n });
        renderCompare();
    }

    /* ── Exports ────────────────────────────────────────────────────────────── */
    function renderExport() {
        var ok = okToSave();
        show('clExportPanel', ok);
        if (ok) $('clExportNote').hidden = !S.verdict.provisional;
    }
    function exportMeta(what) { feat('cl_export', { what: what, provisional: S.verdict && S.verdict.provisional ? 1 : 0, edited: edited() ? 1 : 0 }); }
    function fileBase() { var d = new Date(); return 'input-lag-' + d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0'); }
    /* CSV a spreadsheet opens as columns: where the decimal mark is a comma (ru, de, fr, es, pt,
       id, uk), the separator is a semicolon — Excel's own convention there — and the file starts
       with a BOM so Cyrillic and Arabic labels survive (QA m11). */
    function csv() {
        var dec = (1.5).toLocaleString(intl()).indexOf(',') >= 0 ? ',' : '.', sep = dec === ',' ? ';' : ',';
        var num = function (v) { return v == null || !isFinite(v) ? '' : (Math.round(v * 10) / 10).toFixed(1).replace('.', dec); };
        var st = currentStats(), q = function (s) { s = String(s == null ? '' : s); return /[";,\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
        var meta = [['# tool', 'camera-lag-test'], ['# file', S.file.name || ''], ['# capture_fps', S.tb.captureFps], ['# clock', S.tb.source],
                    ['# median_ms', num(st.median)], ['# q1_ms', num(st.q1)], ['# q3_ms', num(st.q3)], ['# presses', pairsNow().length], ['# used', st.n],
                    ['# provisional', S.verdict.provisional ? 1 : 0], ['# label', $('clLabel').value.trim()], ['# date', new Date().toISOString()]];
        var rows = meta.map(function (r) { return r.map(q).join(sep); });
        rows.push('');
        rows.push(['press', 'press_ms', 'screen_ms', 'latency_ms', 'excluded', 'edited'].join(sep));
        pairsNow().forEach(function (p) { rows.push([p.i + 1, num(p.pressMs), num(p.onsetMs), num(p.latencyMs), p.excluded ? 1 : 0, p.edited ? 1 : 0].join(sep)); });
        return '\uFEFF' + rows.join('\r\n') + '\r\n';
    }
    function cardCanvas() {
        var st = currentStats(), W = 1200, H = 630, c = document.createElement('canvas'); c.width = W; c.height = H;
        var x = c.getContext('2d'), R = rtl(), L = 64, Rx = W - 64, start = R ? Rx : L, end = R ? L : Rx;
        try { x.direction = R ? 'rtl' : 'ltr'; } catch (e) {}
        x.fillStyle = '#10141c'; x.fillRect(0, 0, W, H);
        x.textAlign = R ? 'right' : 'left';
        x.fillStyle = '#e8ecf3'; x.font = '600 34px system-ui, sans-serif'; x.fillText(t('card_title'), start, 92);
        var big = fmtMs(st.median, 0);
        x.fillStyle = '#f5b342'; x.font = '700 168px system-ui, sans-serif'; x.fillText(big, start, 300);
        var w = x.measureText(big).width;
        x.fillStyle = '#e8ecf3'; x.font = '500 48px system-ui, sans-serif'; x.fillText(t('ms'), R ? start - w - 18 : start + w + 18, 300);
        x.fillStyle = '#a9b3c4'; x.font = '400 30px system-ui, sans-serif';
        x.fillText(st.n >= 2 ? t('spread', { lo: fmtMs(st.q1, 0), hi: fmtMs(st.q3, 0), n: st.n, min: fmtMs(st.min, 0), max: fmtMs(st.max, 0) }) : t('spread_one', { n: st.n }), start, 360, W - 128);
        x.fillText(t('precision', { ms: fmtMs(S.tb.frameMs, 1) }) + ' · ' + t('clock_short', { fps: S.tb.captureFps }), start, 404, W - 128);
        var label = $('clLabel').value.trim(); if (label) { x.fillStyle = '#e8ecf3'; x.fillText(label, start, 452, W - 128); }
        if (S.verdict.provisional) { x.fillStyle = '#f5b342'; x.font = '600 26px system-ui, sans-serif'; x.fillText(t('provisional'), start, 492, W - 128); }
        var vals = st.values.slice().sort(function (a, b) { return a - b; }), mx = Math.max(st.max * 1.1, 50);
        var px = function (v) { var u = v / mx; return R ? Rx - (W - 128) * u : L + (W - 128) * u; };
        x.strokeStyle = '#3a4456'; x.lineWidth = 2; x.beginPath(); x.moveTo(L, 540); x.lineTo(Rx, 540); x.stroke();
        vals.forEach(function (v) { x.fillStyle = '#3fc5e8'; x.beginPath(); x.arc(px(v), 540, 9, 0, Math.PI * 2); x.fill(); });
        x.fillStyle = '#a9b3c4'; x.font = '400 22px system-ui, sans-serif';
        x.textAlign = R ? 'right' : 'left'; x.fillText('0', start, 580);
        x.textAlign = R ? 'left' : 'right'; x.fillText(fmtMs(mx, 0) + ' ' + t('ms'), end, 580);
        x.fillText(CFG.brand || '', end, 92);
        return c;
    }
    function exportPng() {
        if (!okToSave()) return;
        var c = cardCanvas();
        window.__canvasBlob(c, 'image/png').then(function (blob) { if (blob && window.__downloadFile) window.__downloadFile(blob, fileBase() + '.png'); })
            .catch(function (e) { if (window.__handleError) window.__handleError(e, 'camera_lag_png', { say: t('export_failed') }); });
        exportMeta('png');
    }
    function exportCsv() {
        if (!okToSave()) return;
        if (window.__downloadFile) window.__downloadFile(new Blob([csv()], { type: 'text/csv;charset=utf-8' }), fileBase() + '.csv');
        exportMeta('csv');
    }
    /* The PDF is TEXT, not a picture of the page: HtmlPdf lays the report out with real fonts —
       selectable, searchable, small (QA m11: the html2canvas raster had no text at all). */
    async function exportPdf() {
        if (!okToSave()) return;
        var btn = $('clPdf');
        if (!window.HtmlPdf) { if (window.__toast) window.__toast(t('export_failed'), 'error'); return; }
        btn.disabled = true;
        var el = $('clReport'), st = currentStats();
        try {
            try { window.HtmlPdf.installFonts(); } catch (e) {}
            el.style.fontFamily = window.HtmlPdf.FAMILY ? window.HtmlPdf.FAMILY.sans : '';
            el.setAttribute('dir', rtl() ? 'rtl' : 'ltr');
            el.querySelector('[data-r=title]').textContent = t('card_title');
            el.querySelector('[data-r=label]').textContent = $('clLabel').value.trim();
            el.querySelector('[data-r=median]').textContent = fmtMs(st.median, 0) + ' ' + t('ms') + (S.verdict.provisional ? ' · ' + t('provisional') : '');
            el.querySelector('[data-r=spread]').textContent = st.n >= 2 ? t('spread', { lo: fmtMs(st.q1, 0), hi: fmtMs(st.q3, 0), n: st.n, min: fmtMs(st.min, 0), max: fmtMs(st.max, 0) }) : t('spread_one', { n: st.n });
            el.querySelector('[data-r=meta]').textContent = t('precision', { ms: fmtMs(S.tb.frameMs, 1) }) + ' · ' + $('clClockLine').textContent + ($('clGame').hidden ? '' : ' · ' + $('clGame').textContent);
            el.querySelector('[data-r=verdict]').textContent = $('clVerdictHead').textContent + ' ' + $('clVerdictMore').textContent;
            var img = el.querySelector('[data-r=trace]'); try { img.src = $('clTrace').toDataURL('image/png'); } catch (e) {}
            var tb = el.querySelector('[data-r=rows]'); tb.textContent = '';
            pairsNow().forEach(function (p) {
                var tr = document.createElement('tr');
                [String(p.i + 1), p.latencyMs == null || p.order ? '—' : fmtMs(p.latencyMs, 1), p.excluded ? t('rv_excluded') : (p.edited ? t('edited') : '')].forEach(function (v) { var td = document.createElement('td'); td.textContent = v; tr.appendChild(td); });
                tb.appendChild(tr);
            });
            el.querySelector('[data-r=date]').textContent = new Date().toLocaleString(intl()) + ' · ' + (CFG.brand || '');
            if (img.src && !img.complete) await new Promise(function (r) { img.onload = img.onerror = r; setTimeout(r, 1500); });
            var bytes = await window.HtmlPdf.fromElement(el, { pages: 'auto', page: 'a4', marginsMm: { t: 16, r: 16, b: 16, l: 16 }, title: t('card_title'), lang: window.__LOCALE || 'en', creator: CFG.brand || '' });
            if (window.__downloadFile) await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileBase() + '.pdf');
            exportMeta('pdf');
        } catch (err) {
            if (window.__handleError) window.__handleError(err, 'camera_lag_pdf', { say: t('export_failed') });
        } finally { btn.disabled = false; }
    }

    /* ── Small DOM helpers ──────────────────────────────────────────────────── */
    function show(id, on) { var el = $(id); if (el) el.hidden = !on; }
    function setProgress(frac, text) {
        var el = $('clProgress'); if (!el) return;
        if (frac < 0) { el.hidden = true; return; }
        el.hidden = false;
        $('clProgressBar').style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%';
        if (text) textWrite($('clProgressText'), text);
    }
    function setPhase(p) {
        if (!S) return;
        S.phase = p;
        $('clWork').setAttribute('data-phase', p);
        if (p === 'ask' && !S.result) showState('ask', t('st_ask'), t(S.decoded ? 'st_ask_next' : 'st_ask_next_reading'), null);
    }
    function resetView() {
        show('clGuide', false); show('clWork', true);
        $('clTop').classList.add('cl-has-file');
        ['clResultPanel', 'clPressPanel', 'clComparePanel', 'clExportPanel', 'clTracePanel', 'clReview', 'clBoxHelp', 'clClock', 'clPre', 'clRange', 'clCancel', 'clView', 'clScrubWrap', 'clBoxBar'].forEach(function (id) { show(id, false); });
        $('clView').classList.remove('cl-placing');
        $('clWork').classList.remove('cl-asking');
        $('clWork').setAttribute('data-phase', 'preflight');
        RV = null; viewSnap = 0; bitmapLane = 'bitmap';
        $('clPreWarn').textContent = ''; fillNotes([]);
        placeBoxes();
        var cv = $('clFrame'); try { cv.getContext('2d').clearRect(0, 0, cv.width, cv.height); } catch (e) {}
    }

    function init() {
        var zone = $('clUpload');
        if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, function (f) { onFile(f); });
        if (zone) zone.addEventListener('tool-uploader:clear', function () {
            finishFile('cleared');
            if (S && S.abort) { try { S.abort(); } catch (e) {} }
            if (S) endRun();
            S = null; RV = null; show('clWork', false); show('clGuide', true); $('clTop').classList.remove('cl-has-file');
        });
        bindBoxes(); bindReview();
        Array.prototype.forEach.call(document.querySelectorAll('[data-cl-fps]'), function (b) {
            b.addEventListener('click', function () { var v = b.getAttribute('data-cl-fps'); setAnswer(v === 'normal' || v === 'file' ? v : +v); });
        });
        $('clClockEdit').addEventListener('click', function () { if (!S) return; S.clockOpen = true; renderClock(); feat('clock_edit_open'); });
        $('clCancel').addEventListener('click', function () { if (S && S.abort) S.abort(); });
        $('clRangeStart').addEventListener('input', updateRangeText);
        $('clRangeGo').addEventListener('click', rangeGo);
        $('clStateRetry').addEventListener('click', function () { if (S && S.file) onFile(S.file); });
        $('clStateReview').addEventListener('click', function () { if (S && S.result && S.result.pairs.length) openReview(0); });
        $('clSave').addEventListener('click', function () { if (saveRun()) renderCompare(); });
        $('clSaveA').addEventListener('click', function () { savePairSide('A'); });
        $('clSaveB').addEventListener('click', function () { savePairSide('B'); });
        $('clClear').addEventListener('click', clearRuns);
        $('clCmpA').addEventListener('change', function () { compareNow(true); }); $('clCmpB').addEventListener('change', function () { compareNow(true); });
        $('clPng').addEventListener('click', exportPng); $('clPdf').addEventListener('click', exportPdf); $('clCsv').addEventListener('click', exportCsv);
        Array.prototype.forEach.call(document.querySelectorAll('[data-cl-pair]'), function (b) {
            b.addEventListener('click', function () {
                var kind = b.getAttribute('data-cl-pair'), cur = lsGet(PAIR_KEY, null);
                lsSet(PAIR_KEY, kind === 'custom' ? null : (cur && cur.kind === kind ? cur : { kind: kind }));
                feat('pair_pick', { kind: kind }); renderCompare();
            });
        });
        window.addEventListener('resize', function () { if (S && S.result) drawTrace(); });
        window.addEventListener('pagehide', function () { finishFile('pagehide'); });
    }
    window.__CameraLagTest = { _state: function () { return S; }, _pairs: pairsNow, _stats: currentStats, _verdict: function () { return S && S.verdict; } };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
